/**
 * Central config for which roles a client may see/assign (e.g. Whistle-internal, Procore).
 * Used in UI to hide roles that do not apply.
 * Backend MUST enforce the same rules on GET /users/roles and when assigning roles (e.g. PATCH user).
 */

/** Client IDs that may see and assign sensitive roles (e.g. Whistle-internal). */
export const PRIVILEGED_CLIENT_IDS_FOR_SENSITIVE_ROLES = (
  process.env.VUE_APP_PRIVILEGED_CLIENT_IDS_FOR_SENSITIVE_ROLES || ""
)
  .split(",")
  .map(Number);

/**
 * Whether a role is considered sensitive (should only be visible/assignable by privileged clients).
 * @param {{ name?: string }} role - Role object (e.g. from API)
 * @returns {boolean}
 */
export function isSensitiveRole(role) {
  if (!role || !role.name) return false;
  return role.name.includes("Whistle");
}

/**
 * Whether the given client is allowed to see and assign sensitive roles.
 * @param {number|string|null|undefined} clientId
 * @returns {boolean}
 */
export function canSeeSensitiveRoles(clientId) {
  if (clientId == null || clientId === undefined) return false;
  return PRIVILEGED_CLIENT_IDS_FOR_SENSITIVE_ROLES.includes(Number(clientId));
}

/**
 * Filter a list of roles to only those the client is allowed to see.
 * @param {Array<{ name?: string }>} roles
 * @param {number|string|null|undefined} clientId - Current user's client or the user being edited's client
 * @returns {Array}
 */
export function filterRolesByClientVisibility(roles, clientId) {
  if (!Array.isArray(roles)) return [];
  const list = roles.slice();
  if (canSeeSensitiveRoles(clientId)) return list;
  return list.filter((r) => !isSensitiveRole(r));
}

/**
 * Whether a role is tied to Procore (hidden unless Procore is enabled for the client).
 * @param {{ name?: string, displayName?: string }} role - Role object (e.g. from API)
 * @returns {boolean}
 */
export function isProcoreRole(role) {
  if (!role) return false;
  const combined = `${role.displayName || ""} ${role.name || ""}`;
  return /procore/i.test(combined);
}

/**
 * Filter roles when Procore integration is off: removes Procore-named roles.
 * @param {Array<{ name?: string, displayName?: string }>} roles
 * @param {boolean} procoreEnabled - Whether Procore is enabled for the relevant client
 * @returns {Array}
 */
export function filterRolesByProcoreVisibility(roles, procoreEnabled) {
  if (!Array.isArray(roles)) return [];
  if (procoreEnabled) return roles.slice();
  return roles.filter((r) => !isProcoreRole(r));
}
